import React from "react";
import { Card } from "../ui/card";
import { Users, Heart, Target, Zap, Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { createPageUrl } from "../../lib/utils";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";

export default function SolutionSection() {
  const benefits = [
    {
      icon: Users,
      title: "Relatable Mentors",
      description:
        "Learn from elite students who've conquered the same classes & challenges you face",
      color: "from-blue-500 to-cyan-500 dark:from-red-500 dark:to-orange-500",
      bgGradient:
        "from-blue-50 to-cyan-50 dark:bg-gradient-to-br dark:from-red-900/40 dark:via-zinc-900 dark:to-orange-900/30",
      borderColor: "border-blue-200 dark:border-red-500/40",
      textColor: "text-blue-700 dark:text-orange-200"
    },
    {
      icon: Heart,
      title: "Personalized Approach",
      description:
        "Every session is tailored to your unique needs, learning style, and pace. No cookie-cutter lessons.",
      color: "from-pink-500 to-rose-500 dark:from-orange-500 dark:to-amber-500",
      bgGradient:
        "from-pink-50 to-rose-50 dark:bg-gradient-to-br dark:from-orange-900/40 dark:via-zinc-900 dark:to-amber-900/30",
      borderColor: "border-pink-200 dark:border-orange-500/40",
      textColor: "text-pink-700 dark:text-amber-200"
    },
    {
      icon: Target,
      title: "Proven Results",
      description:
        "Our tutors understand what it feels like to fall behind—and more importantly, how to rise above it.",
      color: "from-green-500 to-emerald-500 dark:from-red-600 dark:to-orange-600",
      bgGradient:
        "from-green-50 to-emerald-50 dark:bg-gradient-to-br dark:from-red-900/50 dark:via-zinc-900 dark:to-orange-900/40",
      borderColor: "border-green-200 dark:border-red-600/40",
      textColor: "text-green-700 dark:text-orange-200"
    },
    {
      icon: Zap,
      title: "Accessible & Affordable",
      description:
        "High-quality tutoring shouldn't break the bank. At $65/hour, we make excellence accessible to all.",
      color: "from-orange-500 to-amber-500 dark:from-orange-600 dark:to-amber-600",
      bgGradient:
        "from-orange-50 to-amber-50 dark:bg-gradient-to-br dark:from-orange-900/50 dark:via-zinc-900 dark:to-amber-900/40",
      borderColor: "border-orange-200 dark:border-orange-500/50",
      textColor: "text-orange-700 dark:text-amber-200"
    }
  ];

  return (
    <div
      className="
        py-20 relative overflow-hidden transition-colors duration-300
        bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50
        dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950
      "
    >
      {/* Background glows */}
      <div className="absolute inset-0 opacity-30 dark:opacity-40">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-300 dark:bg-red-600/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300 dark:bg-orange-500/30 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="
              inline-flex items-center gap-2 px-6 py-3 rounded-full mb-8 shadow-lg border-2
              bg-gradient-to-r from-blue-100 to-purple-100
              dark:bg-gradient-to-r dark:from-red-500/10 dark:to-orange-500/10
              text-blue-700 dark:text-orange-300
              border-blue-200 dark:border-red-500/40
              backdrop-blur-sm
            "
            whileHover={{ scale: 1.05 }}
          >
            <Target className="w-5 h-5" />
            <span className="font-bold text-lg">Our Solution</span>
          </motion.div>

          <h2
            className="
              text-4xl md:text-6xl font-extrabold mb-6 pb-2
              bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900
              dark:from-red-400 dark:via-orange-400 dark:to-amber-400
              bg-clip-text text-transparent
            "
          >
            Flipping Education on Its Head
          </h2>

          <p className="text-xl md:text-2xl text-gray-600 dark:text-zinc-300 max-w-3xl mx-auto leading-relaxed">
            Instead of standardized teaching, we connect students with{" "}
            <span className="font-bold text-purple-600 dark:text-orange-300">
              college peers
            </span>{" "}
            who have walked the same path and overcome the same obstacles.
          </p>
        </motion.div>

        {/* Benefit cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <Card
                className={`
                  p-8 border-3 transition-all duration-300 group relative overflow-hidden
                  border-transparent hover:border-blue-300
                  dark:hover:border-red-500/40
                  hover:shadow-2xl
                  bg-gradient-to-br ${benefit.bgGradient}
                `}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 dark:from-white/5 transition-opacity duration-300"></div>

                <div className="relative">
                  <div
                    className={`
                      w-16 h-16 rounded-2xl flex items-center justify-center mb-6
                      bg-gradient-to-br ${benefit.color}
                      group-hover:scale-110 group-hover:rotate-6
                      transition-all duration-300 shadow-xl
                    `}
                  >
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-700 dark:text-zinc-300 leading-relaxed text-lg">
                    {benefit.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="
            relative rounded-3xl p-10 md:p-16 shadow-2xl overflow-hidden
            bg-gradient-to-br from-indigo-600 via-purple-600 to-blue-700
            dark:bg-gradient-to-br dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900
            text-white
          "
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Glow */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-10 left-10 w-40 h-40 bg-pink-400 dark:bg-red-500 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-10 right-10 w-60 h-60 bg-blue-400 dark:bg-orange-500 rounded-full blur-3xl animate-pulse delay-700"></div>
          </div>

          <div className="relative">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="w-10 h-10 text-yellow-300 dark:text-amber-400 animate-pulse" />
              <h3 className="text-4xl md:text-5xl font-black">
                Imagine This...
              </h3>
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed mb-10">
              <motion.div
                className="
                  flex items-start gap-4 rounded-2xl p-6 border
                  bg-white/10 backdrop-blur-sm border-white/20
                  dark:bg-zinc-800/60 dark:border-red-500/20
                "
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="w-3 h-3 bg-yellow-300 dark:bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>
                  A high school student mastering <span className="font-bold text-yellow-300 dark:text-amber-300">AP Calculus BC</span>, 
                  guided by a peer tutor who{" "}
                  <span className="font-bold text-yellow-300 dark:text-amber-300">
                    earned a 5 on the AP exam and now studies advanced mathematics
                  </span>.
                </p>
              </motion.div>
              
              <motion.div
                className="
                  flex items-start gap-4 rounded-2xl p-6 border
                  bg-white/10 backdrop-blur-sm border-white/20
                  dark:bg-zinc-800/60 dark:border-orange-500/20
                "
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
              >
                <div className="w-3 h-3 bg-pink-300 dark:bg-orange-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>
                  A student tackling <span className="font-bold text-pink-300 dark:text-orange-300">AP Physics C: Mechanics</span>, 
                  supported by a tutor who{" "}
                  <span className="font-bold text-pink-300 dark:text-orange-300">
                    completed college-level physics and conducts undergraduate research
                  </span>.
                </p>
              </motion.div>

              <motion.div
                className="
                  flex items-start gap-4 rounded-2xl p-6 border
                  bg-white/10 backdrop-blur-sm border-white/20
                  dark:bg-zinc-800/60 dark:border-blue-500/20
                "
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <div className="w-3 h-3 bg-blue-300 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                <p>
                  An aspiring engineer working through <span className="font-bold text-blue-300 dark:text-blue-300">Multivariable Calculus</span>, 
                  mentored by a tutor who{" "}
                  <span className="font-bold text-blue-300 dark:text-blue-300">
                    is currently pursuing a degree in aerospace engineering
                  </span>.
                </p>
              </motion.div>
            </div>

            <motion.div
              className="
                rounded-2xl p-8 shadow-2xl
                bg-gradient-to-r from-yellow-400 to-orange-400
                dark:from-red-500 dark:to-amber-500
              "
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
            >
              <p className="font-black text-2xl md:text-3xl text-gray-900 mb-6">
                By matching learners with those who understand the struggle
                firsthand, education becomes not only more personal—but far more
                attainable.
              </p>

              <Link to={createPageUrl("Bookings")}>
                <Button
                  size="lg"
                  className="
                    bg-gray-900 text-white hover:bg-gray-800
                    dark:bg-black dark:hover:bg-zinc-800
                    shadow-xl group font-bold text-lg
                  "
                >
                  Experience the Difference
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
